/**
 * swfMp3 — extract the streaming MP3 audio out of a Flash SWF.
 *
 * FFR level SWFs encode their music as a series of SoundStreamBlock
 * tags (tag id 19) preceded by a single SoundStreamHead (id 18) or
 * SoundStreamHead2 (id 45). For MP3 streams each block is:
 *   - 2 bytes  sampleCount    (little-endian uint16)
 *   - 2 bytes  seekDelta      (signed int16, may be 0)
 *   - N bytes  raw MP3 frames (one or more)
 *
 * Concatenating the MP3 frame payload from every block yields a
 * playable MP3 byte stream — no transcoding needed, just stitch.
 *
 * The SWF outer container may be uncompressed (FWS), zlib-compressed
 * (CWS) or LZMA-compressed (ZWS). We support FWS + CWS (LZMA is rare
 * in FFR levels — if we hit one we throw and the radio picks another).
 *
 * Pure-browser implementation using DecompressionStream + DataView.
 */

const SIG_FWS = 0x465753;     // "FWS"
const SIG_CWS = 0x435753;     // "CWS"
const SIG_ZWS = 0x5A5753;     // "ZWS"

const TAG_SOUND_STREAM_HEAD  = 18;
const TAG_SOUND_STREAM_BLOCK = 19;
const TAG_SOUND_STREAM_HEAD2 = 45;
const TAG_END                = 0;

/** Decompress a CWS body (zlib format) using the browser's DecompressionStream. */
async function inflateZlib(compressed: Uint8Array): Promise<Uint8Array> {
    const stream = new Blob([compressed]).stream().pipeThrough(
        new DecompressionStream('deflate'),
    );
    const chunks: Uint8Array[] = [];
    const reader = stream.getReader();
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (value) chunks.push(value);
    }
    const total = chunks.reduce((n, c) => n + c.byteLength, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const c of chunks) { out.set(c, offset); offset += c.byteLength; }
    return out;
}

/** Skip the RECT (FrameSize) field at offset `off` of `body`. Returns the
 *  byte offset immediately after the RECT (still need to skip 4 more bytes
 *  for FrameRate + FrameCount). */
function skipRect(body: Uint8Array, off: number): number {
    const nbits = body[off]! >> 3;        // top 5 bits of first byte
    const totalBits = 5 + 4 * nbits;
    return off + Math.ceil(totalBits / 8);
}

/** Extract a contiguous MP3 byte stream from a SWF buffer. Returns the
 *  concatenated MP3 frames, or null if no audio was found. */
export async function extractSwfMp3(buf: ArrayBuffer): Promise<Uint8Array | null> {
    if (buf.byteLength < 12) return null;
    const head = new Uint8Array(buf, 0, 8);
    const sig = (head[0]! << 16) | (head[1]! << 8) | head[2]!;
    const version = head[3]!;
    void version;
    // bytes 4-7 are the uncompressed size; the body starts at byte 8.
    let body: Uint8Array;
    if (sig === SIG_FWS) {
        body = new Uint8Array(buf, 8);
    } else if (sig === SIG_CWS) {
        const compressed = new Uint8Array(buf, 8);
        body = await inflateZlib(compressed);
    } else if (sig === SIG_ZWS) {
        throw new Error('LZMA-compressed SWF (ZWS) not supported');
    } else {
        throw new Error(`Not a SWF: signature ${sig.toString(16)}`);
    }

    // Skip RECT + FrameRate + FrameCount.
    let p = skipRect(body, 0);
    p += 4;                              // 2 bytes FrameRate + 2 bytes FrameCount

    // Walk tags.
    const mp3Chunks: Uint8Array[] = [];
    while (p + 2 <= body.length) {
        const tagHeader = body[p]! | (body[p + 1]! << 8);
        p += 2;
        const tagType = tagHeader >> 6;
        let tagLen   = tagHeader & 0x3F;
        if (tagLen === 0x3F) {
            // Long-form length: next 4 bytes are uint32 LE
            if (p + 4 > body.length) break;
            tagLen = body[p]! | (body[p + 1]! << 8) | (body[p + 2]! << 16) | (body[p + 3]! << 24);
            p += 4;
        }
        if (p + tagLen > body.length) break;
        if (tagType === TAG_END) break;

        if (tagType === TAG_SOUND_STREAM_HEAD || tagType === TAG_SOUND_STREAM_HEAD2) {
            // We don't actually need the head — its fields are encoded into
            // each MP3 frame's own header. Just confirm the format is MP3.
            // Body layout: [playbackFlags] [streamFlags] [sampleCount LE16]
            // streamFlags high nibble = compression: 2 = MP3.
            if (tagLen >= 2) {
                const streamFlags = body[p + 1]!;
                const compression = (streamFlags >> 4) & 0x0F;
                if (compression !== 2) {
                    // Not MP3 — skip but keep walking; some SWFs have both.
                }
            }
        } else if (tagType === TAG_SOUND_STREAM_BLOCK) {
            // MP3 block: 2 bytes sampleCount, 2 bytes seekDelta, then MP3 frames.
            if (tagLen >= 5) {
                const start = p + 4;
                const end   = p + tagLen;
                mp3Chunks.push(body.subarray(start, end));
            }
        }
        p += tagLen;
    }

    if (mp3Chunks.length === 0) return null;
    const total = mp3Chunks.reduce((n, c) => n + c.byteLength, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    for (const c of mp3Chunks) { out.set(c, offset); offset += c.byteLength; }
    return out;
}
